import {
  FollowedCreatorAvailabilityNotification,
  ProfileResponseModel,
} from '../types/Profile';

type UnknownObject = Record<string, unknown>;

const getObjectValue = (obj: UnknownObject, keys: string[]): unknown => {
  for (const key of keys) {
    if (key in obj) {
      return obj[key];
    }
  }
  return undefined;
};

const asObjectArray = (value: unknown): UnknownObject[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(
    (item): item is UnknownObject => !!item && typeof item === 'object'
  );
};

const normalizeCreatorId = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }

  if (value && typeof value === 'object') {
    const obj = value as UnknownObject;
    const id = getObjectValue(obj, [
      'professionalId',
      'ProfessionalId',
      'id',
      'Id',
      'userId',
      'UserId',
    ]);

    if (typeof id === 'string' && id.trim()) {
      return id.trim();
    }
  }

  return null;
};

export const normalizeFollowedCreatorIds = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  const ids = value
    .map((item) => normalizeCreatorId(item))
    .filter((id): id is string => !!id);

  return Array.from(new Set(ids));
};

const normalizeNotificationSettingsFromFollowedCreatorIds = (
  value: unknown
): FollowedCreatorAvailabilityNotification[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  const settings = value
    .map((item) => {
      if (!item || typeof item !== 'object') {
        return null;
      }

      const obj = item as UnknownObject;
      const professionalId = normalizeCreatorId(obj);

      if (!professionalId) {
        return null;
      }

      const enabled = normalizeBoolean(
        getObjectValue(obj, [
          'availabilityNotificationsEnabled',
          'AvailabilityNotificationsEnabled',
          'enabled',
          'Enabled',
        ])
      );

      return {
        professionalId,
        availabilityNotificationsEnabled: enabled ?? true,
      };
    })
    .filter(
      (setting): setting is FollowedCreatorAvailabilityNotification =>
        setting !== null
    );

  const settingsMap = new Map<string, FollowedCreatorAvailabilityNotification>();
  settings.forEach((setting) => settingsMap.set(setting.professionalId, setting));

  return Array.from(settingsMap.values());
};

const normalizeBoolean = (value: unknown): boolean | null => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    if (value.toLowerCase() === 'true') {
      return true;
    }

    if (value.toLowerCase() === 'false') {
      return false;
    }
  }

  return null;
};

const normalizeNotificationSetting = (
  value: unknown
): FollowedCreatorAvailabilityNotification | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const obj = value as UnknownObject;
  const professionalId = normalizeCreatorId(obj);

  if (!professionalId) {
    return null;
  }

  const enabled = normalizeBoolean(
    getObjectValue(obj, [
      'availabilityNotificationsEnabled',
      'AvailabilityNotificationsEnabled',
      'enabled',
      'Enabled',
    ])
  );

  return {
    professionalId,
    availabilityNotificationsEnabled: enabled ?? true,
  };
};

export const normalizeFollowedCreatorNotificationSettings = (
  source: unknown
): FollowedCreatorAvailabilityNotification[] => {
  let rawSettings: unknown = source;

  if (source && typeof source === 'object' && !Array.isArray(source)) {
    const obj = source as UnknownObject;
    rawSettings =
      getObjectValue(obj, [
        'followedCreatorAvailabilityNotifications',
        'FollowedCreatorAvailabilityNotifications',
        'followedCreatorNotificationSettings',
        'FollowedCreatorNotificationSettings',
      ]) ?? source;
  }

  const settings = asObjectArray(rawSettings)
    .map((setting) => normalizeNotificationSetting(setting))
    .filter(
      (setting): setting is FollowedCreatorAvailabilityNotification =>
        setting !== null
    );

  const settingMap = new Map<string, FollowedCreatorAvailabilityNotification>();

  settings.forEach((setting) => {
    settingMap.set(setting.professionalId, setting);
  });

  return Array.from(settingMap.values());
};

export const normalizeProfileFollowData = (
  profile: ProfileResponseModel
): ProfileResponseModel => {
  const profileAsObject = profile as unknown as UnknownObject;
  const followedCreatorsRaw = getObjectValue(profileAsObject, [
    'followedCreatorIds',
    'FollowedCreatorIds',
  ]);
  const followedCreatorIds = normalizeFollowedCreatorIds(followedCreatorsRaw);

  const settings = normalizeFollowedCreatorNotificationSettings(profileAsObject);
  const settingsFromFollowedCreators =
    normalizeNotificationSettingsFromFollowedCreatorIds(followedCreatorsRaw);
  const settingsMap = new Map<string, FollowedCreatorAvailabilityNotification>();

  settingsFromFollowedCreators.forEach((setting) => {
    settingsMap.set(setting.professionalId, setting);
  });

  settings.forEach((setting) => {
    settingsMap.set(setting.professionalId, setting);
  });

  followedCreatorIds.forEach((professionalId) => {
    if (!settingsMap.has(professionalId)) {
      settingsMap.set(professionalId, {
        professionalId,
        availabilityNotificationsEnabled: true,
      });
    }
  });

  return {
    ...profile,
    followedCreatorIds,
    followedCreatorAvailabilityNotifications: followedCreatorIds
      .map((professionalId) => settingsMap.get(professionalId))
      .filter(
        (
          setting
        ): setting is FollowedCreatorAvailabilityNotification => !!setting
      ),
  };
};

export const setCreatorFollowStateInProfile = (
  profile: ProfileResponseModel,
  professionalId: string,
  isFollowed: boolean
): ProfileResponseModel => {
  const normalized = normalizeProfileFollowData(profile);
  const currentIds = normalized.followedCreatorIds ?? [];
  const currentSettings =
    normalized.followedCreatorAvailabilityNotifications ?? [];

  const followedCreatorIds = isFollowed
    ? Array.from(new Set([...currentIds, professionalId]))
    : currentIds.filter((id) => id !== professionalId);

  const settingsMap = new Map<string, FollowedCreatorAvailabilityNotification>();

  currentSettings.forEach((setting) => {
    settingsMap.set(setting.professionalId, setting);
  });

  if (isFollowed) {
    settingsMap.set(professionalId, {
      professionalId,
      availabilityNotificationsEnabled: true,
    });
  } else {
    settingsMap.delete(professionalId);
  }

  const followingDelta = isFollowed ? 1 : -1;
  const following = Math.max(0, (normalized.following ?? 0) + followingDelta);

  return {
    ...normalized,
    following,
    followedCreatorIds,
    followedCreatorAvailabilityNotifications: followedCreatorIds
      .map((id) => settingsMap.get(id))
      .filter(
        (
          setting
        ): setting is FollowedCreatorAvailabilityNotification => !!setting
      ),
  };
};

export const setCreatorAvailabilityNotificationInProfile = (
  profile: ProfileResponseModel,
  professionalId: string,
  enabled: boolean
): ProfileResponseModel => {
  const normalized = normalizeProfileFollowData(profile);
  const followedCreatorIds = normalized.followedCreatorIds ?? [];

  if (!followedCreatorIds.includes(professionalId)) {
    return normalized;
  }

  const settingsMap = new Map<string, FollowedCreatorAvailabilityNotification>();

  (normalized.followedCreatorAvailabilityNotifications ?? []).forEach(
    (setting) => {
      settingsMap.set(setting.professionalId, setting);
    }
  );

  settingsMap.set(professionalId, {
    professionalId,
    availabilityNotificationsEnabled: enabled,
  });

  return {
    ...normalized,
    followedCreatorAvailabilityNotifications: followedCreatorIds
      .map((id) => settingsMap.get(id))
      .filter(
        (
          setting
        ): setting is FollowedCreatorAvailabilityNotification => !!setting
      ),
  };
};

export const getCreatorNotificationEnabledFromProfile = (
  profile: ProfileResponseModel | null | undefined,
  professionalId: string
): boolean | undefined => {
  if (!profile) {
    return undefined;
  }

  const normalized = normalizeProfileFollowData(profile);
  const setting = (
    normalized.followedCreatorAvailabilityNotifications ?? []
  ).find((item) => item.professionalId === professionalId);

  return setting?.availabilityNotificationsEnabled;
};
